import Axios from 'axios'
import { createCheckoutService } from '@bigcommerce/checkout-sdk'
import {
  Checkout, Product, Cart,
} from '@/services'
import { EXTEND_SKU } from '@/data/constants'
import UsStates from '@/data/UsStates'

import {
  isSameAddress,
  roundToNDigits,
  gaProducts,
} from '@/helpers'
import { getCustomerDetails } from './common'

const groupBy = (field) => (collection) => collection.reduce((hash, element) => ({
  ...hash, [element[field]]: (hash[element[field]] || []).concat(element),
}), {})
const groupByAddressOne = groupBy('address1')

const pluck = (field) => (collection) => collection.map((element) => element[field])

export const createConsignments = (addresses, items) => {
  const grouped = groupByAddressOne(addresses)
  const itemIDs = items.map((i) => i.id)
  const flattenedConsignments = Object.keys(grouped).map(
    (address) => ({ id: address, lineItems: grouped[address].map((i) => ({ itemId: i.itemID })) }),
  )
  if (flattenedConsignments.length === 1) {
    flattenedConsignments[0].lineItems = itemIDs
  }
  return flattenedConsignments
}

export const createShippingMethodUpdatePayload = pluck('selectedShippingOption')



export const checkoutStepsGA = async (step, lineItems, pageName) => {
  try {
    const customer = getCustomerDetails()
    const customerId = customer?.customerId
    const customerEmail = customer?.customerEmail
    const visitorType = customerEmail ? "Logged In" : "Guest"
    window.dataLayer.push({
      event: 'checkout',
      ecommerce: {
        currencyCode: 'USD',
        checkout: {
          actionField: { step },
          products: await gaProducts(lineItems),
        },
      },
      customerEmail,
      visitorType,
      customerId,
      pageName,
    })
  } catch (error) {
    console.error(error)
  }
}

export function scrollToTop() {
  window?.scrollTo({ top: 0, behavior: 'smooth' })
}

export function formatProductOptions(options) {
  return options.reduce((productOptions, option, i) => `${productOptions
    }${i === 0 ? ': ' : ''} ${option.value.split('~')[0]}${i !== options.length - 1 ? ',' : ''}`, '')
}

export function formatPhoneNumber(phoneNumberString) {
  const cleaned = (`${phoneNumberString}`).replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`
  }
  return null
}

export function shippingErrorClass(addressErrors, index = 0) {
  const shippingStateError = 'We do not support shipping to Alaska and Hawaii.'
  const nonShippingStateError = addressErrors && addressErrors[index]?.stateOrProvinceCode?.message.includes(shippingStateError)
  return !!addressErrors && nonShippingStateError &&
    !addressErrors[index]?.city?.message &&
    !addressErrors[index]?.postalCode?.message
}

const isAddedProduct = ({ productId, variantId, existingShipmentForThisStore }) => (item) => (
  productId === item.productId
  && variantId === item.variantId
  && (
    existingShipmentForThisStore
      ? (!existingShipmentForThisStore?.lineItemIds.includes(item.id))
      : (true)
  )
)

const itemToBigCommerceLineItems = ({ quantity, id: itemId }) => ({ quantity, itemId })

const emptyAddress = () => ({
  firstName: '',
  lastName: '',
  email: '',
  company: '',
  address1: '',
  address2: '',
  city: '',
  stateOrProvince: '',
  stateOrProvinceCode: '',
  countryCode: '',
  shouldSaveAddress: '',
  postalCode: '',
  phone: '',
  customFields: [
    {
      fieldId: Checkout.BOPUS_FIELD,
      fieldValue: '0',
    },
  ],
})
const bopusAddress = (store) => ({
  firstName: 'ZGallerie',
  lastName: (store.name || store.store_name).replace('Z Gallerie', ''),
  email: store.email || 'customerservice@zgallerie.com',
  company: 'ZGallerie',
  address1: store.addr1,
  address2: store.addr2,
  city: store.city,
  countryCode: 'US',
  stateOrProvince: UsStates.find((state) => state.abbreviation === store.state)?.name,
  stateOrProvinceCode: store.state,
  shouldSaveAddress: false,
  postalCode: store.zip_cd,
  phone: store.phone || '424.999.4626',
  customFields: [
    {
      fieldId: Checkout.BOPUS_FIELD,
      fieldValue: '1',
    },
    {
      fieldId: Checkout.BOPUS_STORE_FIELD,
      fieldValue: (store.store_cd).toString(),
    },
  ],
})

export const addBOPISProduct = async ({
  quantity,
  productId,
  optionId,
  onClose,
  store,
  onUpdateCart,
  onCartAdded,
  variantId,
  updateHeight,
}) => {
  const data = {
    lineItems: [
      {
        quantity,
        productId,
        variantId: variantId || undefined,
      },
    ],
  }
  const shippingAddress = bopusAddress(store)
  shippingAddress.address2 = (
    shippingAddress.address2 ? `${shippingAddress.address2}_` : '_'
  )
  let checkoutId = ''
  const cart = await Cart.content()
  let cartData
  if (!cart) {
    cartData = await Cart.createCart(data)
    checkoutId = cartData.id
  } else {
    checkoutId = cart.id
    const lineItems = [{
      productId,
      quantity,
      variantId: variantId || undefined,
    }]
    cartData = await Cart.storefrontAddItems(checkoutId, lineItems)
  }
  // After adding to cart we either create a new consignment or update an existing one
  const { consignments } = await Checkout.getCheckout({ cartId: checkoutId })
  const existingShipmentForThisStore = (
    consignments
      ?.find((c) => isSameAddress(shippingAddress, c.shippingAddress))
  )

  const { physicalItems } = cartData.lineItems
  let existingLineItemsInConsignment = []
  if (existingShipmentForThisStore) { // Update existing consignment
    existingLineItemsInConsignment = physicalItems.filter(
      (item) => existingShipmentForThisStore?.lineItemIds.includes(item.id),
    )
  }
  // Get newly added item to cart
  const incomingItem = physicalItems.find(isAddedProduct({ productId, variantId, existingShipmentForThisStore }))
  // Get pre-existing item that is equal to the one added
  const sameItemAlreadyInTheConsignment = physicalItems.find((item) => (
    productId === item.productId
    && variantId === item.variantId
    && existingShipmentForThisStore?.lineItemIds.includes(item.id)
  ))

  const incomingItems = [incomingItem].filter((item) => !!item && !existingLineItemsInConsignment.find((existingItem) => item?.id === existingItem.id))

  const lineItemsForNewConsignment = [...existingLineItemsInConsignment, ...incomingItems].map(itemToBigCommerceLineItems)
  const requestBody = [{
    lineItems: lineItemsForNewConsignment,
    shippingAddress,
  }]
  await Checkout.createConsignments(checkoutId, requestBody)
  onUpdateCart(await Cart.content())
  !updateHeight && onClose()

  // pre-existing item takes precedence since the incoming item disappears once added to same consignment as existing item
  const cartAddition = {
    cart_item: {
      id: (sameItemAlreadyInTheConsignment || incomingItem).id,
      product_id: productId,
      thumbnail: (sameItemAlreadyInTheConsignment || incomingItem).imageUrl,
      url: (sameItemAlreadyInTheConsignment || incomingItem).url,
    },
    quantity,
    attributeId: optionId || '',
    comingFromBOPUS: true,
  }

  onCartAdded(cartAddition)
}

export const bounceXPixel = async (order, lineItems, itemsSubtotal, shippingCost, taxTotal, grandTotal) => {
  const {
    orderId, billingAddress, coupons, discountAmount,
  } = order
  window.top.bouncex = window.top.bouncex || []
  window.top.bouncex.push(['conversion', {
    order_id: orderId,
    email: billingAddress.email,
    phone: billingAddress.phone,
    goal: 'purchase',
    transaction_origin: 'online',
    currency: 'USD',
    coupon: [coupons[0]?.code || ''],
    total_discount: discountAmount + coupons[0]?.discountedAmount || 0,
    tax: taxTotal,
    shipping: shippingCost,
    amount: grandTotal,
    item: lineItems.map((lineItem) => ({
      sku: lineItem.sku,
      product_id: lineItem.id,
      price: lineItem.salePrice,
      quantity: lineItem.quantity,
    })),
  },
  ])
}

export const getProductOriginalPriceBySku = async (sku) => {
  try {
    const parentProduct = (await Product.bySku({ sku }))[0]
    const allVariants = parentProduct.variants
    const variant = allVariants.find((variant) => variant.sku === sku)
    const { price } = variant
    return price
  } catch (error) {
    console.log('Error in getting product original price. Returning undefined', error)
    return undefined
  }
}
/**
 * BigCommerce retains the shipping address associated with the current
 * session, which causes issues when user logs in and out. Calling this
 * function clears it. However, bluntly logging out removes user cart as
 * well. Therefore, we log user out, and then restore their shopping cart.
 * In case where second step failed, we delete the shopping cart since there
 * is no way to log them back in automatically.
 */
export const clearSavedAddressOnCheckout = async () => {
  let cartCheckoutId
  try {
    const cart = (await Axios.get('/api/storefront/carts')).data[0]
    const itemsInCart = Object.values(cart.lineItems).reduce((allItems, itemGroup) => ([...allItems, ...itemGroup]), [])
    const checkout = (await Axios.get(`/api/storefront/checkouts/${cart.id}?include=consignments.availableShippingOptions`)).data
    const { consignments } = checkout
    cartCheckoutId = cart.id
    const service = await createCheckoutService()
    await service.loadCheckout(cartCheckoutId)
    await service.signOutCustomer()
    const consignmentsRequestObject = consignments.map((consignment) => {
      const isBopus = consignment?.availableShippingOptions?.[0]?.description === 'Zgallerie (BOPS)'
      return {
        shippingAddress: isBopus ? consignment.shippingAddress : emptyAddress(),
        lineItems: consignment.lineItemIds.map((itemId) => ({
          itemId,
          quantity: itemsInCart.find((itemInCart) => itemInCart.id === itemId).quantity,
        })),
      }
    })
    await Axios.post(`/api/storefront/checkouts/${cartCheckoutId}/consignments?include=consignments.availableShippingOptions`, consignmentsRequestObject)
  } catch (error) {
    console.log('Error in clearing saved address associated with the order,'
      + 'in checkoutHelpers function clearSavedAddressOnCheckout. ', +'Now deleting user cart', error)
    try {
      await Axios.delete(`/api/storefront/checkouts/${cartCheckoutId}`)
    } catch {
      console.log('Fallback deleting user cart failed. This is bad.')
    }
  }
}


/**
 * Parse the HTML response from bigcommerce internal API and extract necessary info required
 * to create a payment method for a signed in customer
 *
 * @param htmlString {string} - The full HTML response from /account.php?action=add_payment_method&provider=braintree&method_type=CARD
 *
 * @returns {Object} cred - the credentials required for bigC payment gateway
 * @returns {string} cred.paymentsUrl
 * @returns {string} cred.customerId
 * @returns {string} cred.vaultToken
 * @returns {string} cred.storeHash
 *
 */

/**
 * Given an array of lineItems, calculate the discount from both sales and coupons.
 *
 * See REQUIRED properties below:
 * Note: there are two types of possible coupon discount data structure.
 * 1. discounts: [{name: '', discountedAmount: 10}, {}]
 * 2. discounts: {coupon: 10}
 *
 * @param {Object[]} lineItems
 * @param {Object[] | Object} lineItems[].discounts
 * @param {number} lineItems[].discounts[].discountedAmount | lineItems[].discount.coupon
 * @param {number} lineItems[].originalPrice
 * @param {number} lineItems[].salePrice
 * @param {number} lineItems[].quantity
 * @returns {number}
 */
export const calculateTotalDiscountFromLineItems = (lineItems) => {
  try {
    return roundToNDigits(lineItems.reduce((sum, item) => {
      const couponDiscountTotal = item?.discounts?.reduce?.((couponSum, coupon) => (
        couponSum + coupon.discountedAmount
      ), 0) || item?.discounts?.coupon || 0
      const saleDiscountTotal = (item.originalPrice - item.salePrice) * item.quantity
      return sum + couponDiscountTotal + saleDiscountTotal
    }, 0))
  } catch (error) {
    console.error('Error in calculating total discount, returning undefined. Error:', error)
    return 0
  }
}

/**
 * Calculate the single item price after factoring in the promo code and quantity
 *
 * Note: there are 3 types of possible coupon discount data structure.
 * 1. discounts: [{name: '', discountedAmount: 10}, ...]
 * 2. discounts: {coupon: 10}
 * 3. discounts: []
 *
 * @param {Object} item
 * @param {number} item.extendedListPrice -
 * @param {number} item.quantity
 * @param {Object|Object[]} item.discounts - the discount object or an array of discount object
 * @param {number} [item.discounts.coupon] - First type of discounts object: The amount of coupon being applied in
 *  TOTAL (considering the quantity)
 * @param {number} [item.discounts[].discountedAmount] - Second type of discounts object: The amount of coupon being
 * applied in TOTAL (considering the quantity)
 * @param {Array} [item.discounts] - Third type of discounts object: In this case, it is just an empty array
 *
 * @returns {number} - a number rounded to max 2 digits after decimal points (e.g. 0.3, 10.44, 2)
 */
export const calculatePriceAfterPromoCodeAndDTP = ({
  extendedListPrice,
  quantity,
  discounts,
}) => {
  const totalDiscount = discounts?.reduce?.((couponSum, coupon) => {
    return couponSum + coupon.discountedAmount
  }, 0) || discounts?.coupon || 0
  const price = extendedListPrice - totalDiscount
  const rounded = Math.round((price + Number.EPSILON) * 100) / 100 // EPSILON ensures accuracy
  return rounded
}

export const getSubtotalPrice = ({ price, quantity }) => {
  return parseFloat((price * quantity).toFixed(2))
}

export const EXTEND_LOGO = 'https://cdn11.bigcommerce.com/s-whvhnri10h/product_images/uploaded_images/extend-logo.png';

export const getCustomItems = ({ lineItems, customItems }) => {
  customItems.map(item => {
    if (item?.sku?.includes(EXTEND_SKU)) {
      const warrantyProductId = item?.sku?.split(EXTEND_SKU)?.[1];
      const warrantyProduct = lineItems.find(lineItem => lineItem.sku === warrantyProductId)
      item.isFurniture = warrantyProduct?.isFurniture
      item.imageUrl = EXTEND_LOGO;
    }
    item.originalPrice = item.listPrice
    item.salePrice = item.listPrice;
    item.extendedSalePrice = item.extendedListPrice;
    item.subtotalPrice = getSubtotalPrice({ price: item.listPrice, quantity: item.quantity })
  })
  return customItems
}

export const isOrderConfirmationPage = () => !!`${window?.location?.href}`.match(/checkout\/order-confirmation\/([a-z0-9]*)$/gi)
